import { FC, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs, { Dayjs } from "dayjs";
import { useMutation } from "react-query";
import { debounce } from "lodash";
import { ModalA } from "../../common/components/ModalA";
import { ButtonA } from "../../common/components/ButtonA";
import {
  fetchUpsertTaskCounterSubmission,
  fetchDeleteTaskCounterSubmission,
} from "../miscellaneous/fetchTaskCounterSubmission";
import {
  TaskCounterLocalRow,
  TaskCounterUserTemplate,
} from "../types/TaskCounterTypes";

type Props = {
  row: TaskCounterLocalRow;
  templates: TaskCounterUserTemplate[];
  selectedDate: Dayjs;
  updateRow: (localId: string, updates: Partial<TaskCounterLocalRow>) => void;
  removeRow: (localId: string) => void;
};

const cancelBtnStyle = {
  backgroundColor: "#fff",
  color: "#172952",
  width: "100px",
  fontWeight: 600,
  border: "1.5px solid",
  borderRadius: "50px",
  textTransform: "capitalize",
};

const labelOptionStyle = {
  pointerEvents: "none",
  fontWeight: 600,
  color: "#FF0000",
};

export const TaskCounterRow = forwardRef<HTMLTableRowElement, Props>(
  ({ row, templates, selectedDate, updateRow, removeRow }, ref) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const savingLockRef = useRef(false);
    const pendingSaveRef = useRef(false);

    const mutationUpsert = useMutation(fetchUpsertTaskCounterSubmission, {
      onSuccess: (res) => {
        if (res.status === 200) {
          const data = res.data;
          updateRow(row.localId, {
            persistedId: data.id,
            lastModifiedTime: data.lastModifiedTime,
            saveStatus: "success",
            saveError: null,
            isDirty: false,
            isSaving: false,
          });
        }
        savingLockRef.current = false;
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          triggerSave();
        }
      },
      onError: (error: any) => {
        const msg = error?.response?.data ?? "Save failed";
        updateRow(row.localId, {
          saveStatus: "error",
          saveError: typeof msg === "string" ? msg : JSON.stringify(msg),
          isSaving: false,
        });
        savingLockRef.current = false;
      },
    });

    const mutationDelete = useMutation(fetchDeleteTaskCounterSubmission, {
      onSuccess: () => {
        removeRow(row.localId);
      },
    });

    const isRowValid = useCallback(() => {
      return !!row.taskCounterTemplateId;
    }, [row.taskCounterTemplateId]);

    const triggerSave = useCallback(() => {
      if (!isRowValid()) return;
      if (savingLockRef.current) {
        pendingSaveRef.current = true;
        return;
      }
      savingLockRef.current = true;
      updateRow(row.localId, {
        saveStatus: "saving",
        isSaving: true,
      });
      mutationUpsert.mutate({
        id: row.persistedId,
        taskCounterTemplateId: row.taskCounterTemplateId,
        workDate: selectedDate.format("YYYY-MM-DD"),
        value: parseInt(row.value) || 0,
        comment: row.comment,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      row.localId,
      row.persistedId,
      row.taskCounterTemplateId,
      row.value,
      row.comment,
      selectedDate,
      isRowValid,
    ]);

    // Keep a ref to the latest triggerSave so the debounced function is stable
    const triggerSaveRef = useRef(triggerSave);
    triggerSaveRef.current = triggerSave;

    // Debounced save - 2 seconds (stable, never recreated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSave = useCallback(
      debounce(() => {
        triggerSaveRef.current();
      }, 2000),
      []
    );

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        debouncedSave.cancel();
      };
    }, [debouncedSave]);

    const handleFieldChange = (
      field: keyof TaskCounterLocalRow,
      value: any,
      extra?: Partial<TaskCounterLocalRow>
    ) => {
      updateRow(row.localId, {
        [field]: value,
        isDirty: true,
        ...extra,
      });
      // Schedule debounced save
      debouncedSave();
    };

    const handleTemplateSelect = (
      _: any,
      template: TaskCounterUserTemplate | null
    ) => {
      if (!template) return;
      if (template.taskType !== 2) return; // Label not selectable
      updateRow(row.localId, {
        taskCounterTemplateId: template.id,
        taskName: template.taskName,
        taskType: template.taskType,
        unitOfMeasure:
          template.unitOfMeasure === 1
            ? "Items"
            : template.unitOfMeasure === 2
            ? "Minutes"
            : null,
        isDirty: true,
        touched: { ...row.touched, taskName: true },
      });
      debouncedSave();
    };

    const handleDelete = () => {
      setShowDeleteModal(false);
      if (row.persistedId) {
        mutationDelete.mutate(row.persistedId);
      } else {
        removeRow(row.localId);
      }
    };

    const showTaskNameError =
      row.touched.taskName && !row.taskCounterTemplateId;
    const showValueError = false;

    return (
      <>
        <TableRow ref={ref} hover>
          {/* Task Name */}
          <TableCell width={180}>
            <Autocomplete
              size="small"
              options={templates}
              getOptionLabel={(opt) => opt.taskName ?? ""}
              renderOption={(props, opt) => (
                <li
                  {...props}
                  key={opt.id}
                  style={opt.taskType !== 2 ? (labelOptionStyle as any) : {}}
                >
                  {opt.taskName}
                </li>
              )}
              value={
                templates.find((t) => t.id === row.taskCounterTemplateId) ??
                null
              }
              onChange={handleTemplateSelect}
              onBlur={() => {
                updateRow(row.localId, {
                  touched: { ...row.touched, taskName: true },
                });
                debouncedSave.flush();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Task"
                  error={showTaskNameError}
                />
              )}
              sx={{ width: "100%" }}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </TableCell>

          {/* Value */}
          <TableCell width={50}>
            <TextField
              size="small"
              value={row.value}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
                handleFieldChange("value", val);
              }}
              onBlur={() => {
                updateRow(row.localId, {
                  touched: { ...row.touched, value: true },
                });
                debouncedSave.flush();
              }}
              error={showValueError}
              placeholder="0"
              sx={{ width: 90 }}
              inputProps={{ inputMode: "numeric" }}
            />
          </TableCell>

          {/* Unit of Measure (read-only) */}
          <TableCell width={100}>{row.unitOfMeasure ?? ""}</TableCell>

          {/* Comment */}
          <TableCell width={140}>
            <TextField
              size="small"
              value={row.comment}
              onChange={(e) => handleFieldChange("comment", e.target.value)}
              onBlur={() => debouncedSave.flush()}
              placeholder="Optional"
              multiline
              maxRows={2}
              sx={{ width: "100%" }}
            />
          </TableCell>

          {/* Last Modified Time */}
          <TableCell width={140}>
            {row.lastModifiedTime
              ? dayjs(row.lastModifiedTime).format("MM/DD/YYYY HH:mm:ss")
              : ""}
          </TableCell>

          {/* Delete */}
          <TableCell width={70} align="center">
            {row.saveStatus !== "saving" && <IconButton
              size="small"
              onClick={() => setShowDeleteModal(true)}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>}
            {row.saveStatus === "saving" && (
              <span className="flex items-center justify-center gap-1">
                <CircularProgress size={16} sx={{ color: "#203c77" }} />
                <span style={{ fontSize: 12 }}>Saving...</span>
              </span>
            )}
            {row.saveStatus === "error" && (
              <Tooltip title={row.saveError ?? "Save failed"} arrow>
                <CancelIcon sx={{ color: "#F44336", fontSize: 22 }} />
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <ModalA
            title="Delete Task"
            isOpen={showDeleteModal}
            onCloseModal={() => setShowDeleteModal(false)}
          >
            <div className="p-4 text-center">
              <p>Are you sure you want to delete this task?</p>
              <div className="flex flex-row mt-5 justify-center gap-3">
                <ButtonA
                  name="Cancel"
                  customStyle={cancelBtnStyle}
                  onHandleClick={() => setShowDeleteModal(false)}
                />
                <ButtonA name="Delete" onHandleClick={handleDelete} />
              </div>
            </div>
          </ModalA>
        )}
      </>
    );
  }
);

TaskCounterRow.displayName = "TaskCounterRow";
